import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SidebarConfig } from '@/config/sidebarConfig';
import { adminSidebar } from '@/config/sidebarConfig';
import { approveRefundRequestApi, listRefundRequestsApi, rejectRefundRequestApi } from '@/lib/api/paymentsApi';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, formatEur } from '@shared';
import { RefundRequestStatus } from '@shared/enums/domain.enum';
import type { RefundRequest } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

const STATUS_CONFIG: Record<RefundRequestStatus, { label: string; bg: string; text: string }> = {
  PENDING: { label: 'En attente', bg: 'bg-amber-100', text: 'text-amber-700' },
  APPROVED: { label: 'Approuvée', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  REJECTED: { label: 'Refusée', bg: 'bg-rose-100', text: 'text-rose-700' },
};

function fullName(u?: { firstName: string | null; lastName: string | null; email: string } | null): string {
  if (!u) return '—';
  const n = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return n || u.email;
}

export default function AdminRefundRequests({ sidebar = adminSidebar }: { sidebar?: SidebarConfig } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const userRole = useAuthStore(s => s.user?.role);
  const qc = useQueryClient();

  const statusFilter = (searchParams.get('status') ?? '') as RefundRequestStatus | '';

  const [pendingReject, setPendingReject] = useState<RefundRequest | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'refund-requests', { status: statusFilter }],
    queryFn: () => listRefundRequestsApi(statusFilter ? { status: statusFilter } : undefined),
    enabled: isAuthenticated,
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => approveRefundRequestApi(id),
    onSuccess: () => {
      toast.success('Demande approuvée');
      qc.invalidateQueries({ queryKey: ['admin', 'refund-requests'] });
    },
    onError: () => toast.error("Erreur lors de l'approbation"),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, decisionNote }: { id: number; decisionNote: string }) =>
      rejectRefundRequestApi(id, { decisionNote }),
    onSuccess: () => {
      toast.success('Demande refusée');
      setPendingReject(null);
      setRejectNote('');
      qc.invalidateQueries({ queryKey: ['admin', 'refund-requests'] });
    },
    onError: () => toast.error('Erreur lors du refus'),
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const pendingCount = data?.meta.pendingCount ?? 0;
  const approvedCount = rows.filter(r => r.status === 'APPROVED').length;
  const rejectedCount = rows.filter(r => r.status === 'REJECTED').length;

  function updateFilter(status: string) {
    const next = new URLSearchParams(searchParams);
    if (status) next.set('status', status);
    else next.delete('status');
    setSearchParams(next, { replace: true });
  }

  return (
    <DashboardLayout sidebarConfig={sidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader
          title='Demandes de remboursement'
          subtitle='Suivi des demandes : validation par BoulesConnect, puis remboursement Stripe déclenché manuellement si nécessaire'
        />

        <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
          <StatCard
            icon={Clock}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='En attente'
            value={String(pendingCount)}
            subtitle='À traiter'
          />
          <StatCard
            icon={Check}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Approuvées (vue actuelle)'
            value={String(approvedCount)}
            subtitle='Refund Stripe à déclencher'
          />
          <StatCard
            icon={X}
            iconBg='bg-rose-100'
            iconColor='text-rose-600'
            label='Refusées (vue actuelle)'
            value={String(rejectedCount)}
          />
        </div>

        {isError && <QueryErrorState onRetry={() => void refetch()} />}

        <div className='bg-white rounded-2xl border border-slate-200'>
          <div className='flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-slate-200'>
            <div className='flex items-center gap-2'>
              <h2 className='text-lg font-bold text-slate-900'>Demandes</h2>
              {total > 0 && (
                <span className='px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full'>
                  {total}
                </span>
              )}
            </div>
            <select
              value={statusFilter}
              onChange={e => updateFilter(e.target.value)}
              className='px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white'
            >
              <option value=''>Tous les statuts</option>
              <option value='PENDING'>En attente</option>
              <option value='APPROVED'>Approuvées</option>
              <option value='REJECTED'>Refusées</option>
            </select>
          </div>

          {isLoading ? (
            <div className='p-6 space-y-3'>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className='h-16 animate-pulse rounded-xl bg-slate-100' />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
              <RotateCcw className='w-10 h-10 opacity-30' />
              <p className='text-sm font-medium'>Aucune demande</p>
            </div>
          ) : (
            <div className='divide-y divide-slate-100'>
              {rows.map(r => {
                const cfg = STATUS_CONFIG[r.status];
                const busy = approveMutation.isPending && approveMutation.variables === r.id;
                return (
                  <div key={r.id} className='px-6 py-4 hover:bg-slate-50/60 transition'>
                    <div className='flex items-start justify-between gap-6 flex-wrap'>
                      <div className='flex-1 min-w-[300px]'>
                        <div className='flex items-center gap-3 mb-1.5'>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                            {cfg.label}
                          </span>
                          <span className='text-xs text-slate-400'>{formatDateAbbreviatedFr(r.createdAt)}</span>
                          <span className='text-xs font-mono text-slate-400'>Paiement #{r.paymentId}</span>
                        </div>
                        <p className='text-sm font-semibold text-slate-900 mb-0.5'>
                          {r.team?.name ?? '—'}{' '}
                          <span className='text-slate-400 font-normal'>· {r.organization?.name ?? '—'}</span>
                        </p>
                        <p className='text-xs text-slate-500 mb-2'>
                          Demandé par {fullName(r.requester)} ({r.requester?.role})
                          {r.payment && (
                            <>
                              {' · '}
                              <span className='font-semibold text-slate-700'>{formatEur(r.payment.amount)}</span>
                            </>
                          )}
                        </p>
                        <p className='text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 whitespace-pre-wrap'>
                          {r.reason}
                        </p>
                        {r.decisionNote && (
                          <p className='mt-2 text-xs text-slate-500 italic'>
                            Note admin : {r.decisionNote}
                            {r.reviewedBy && ` — ${fullName(r.reviewedBy)}`}
                          </p>
                        )}
                      </div>

                      {r.status === 'PENDING' && userRole === 'SUPER_ADMIN' && (
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => approveMutation.mutate(r.id)}
                            disabled={busy}
                            className='flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50'
                          >
                            <Check className='w-4 h-4' />
                            Approuver
                          </button>
                          <button
                            onClick={() => setPendingReject(r)}
                            disabled={busy}
                            className='flex items-center gap-1.5 px-4 py-2 bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 rounded-lg text-sm font-semibold transition'
                          >
                            <X className='w-4 h-4' />
                            Refuser
                          </button>
                        </div>
                      )}

                      {r.status === 'APPROVED' && r.payment?.refundedAt == null && (
                        <a
                          href={`${userRole === 'ORGANIZATION_ADMIN' ? '/organizer' : '/admin'}/payments?focus=${r.paymentId}`}
                          className='text-xs font-semibold text-blue-600 hover:underline self-center'
                        >
                          → Voir le paiement
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {pendingReject && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={() => setPendingReject(null)} />
          <div className='relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6'>
            <h3 className='text-lg font-bold text-slate-900 mb-2'>Refuser la demande #{pendingReject.id}</h3>
            <p className='text-sm text-slate-500 mb-4'>Motif obligatoire (sera visible par le demandeur).</p>
            <textarea
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              rows={4}
              minLength={10}
              maxLength={2000}
              className='w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:outline-none'
              placeholder='Ex: demande hors délai contractuel…'
            />
            <div className='flex items-center justify-end gap-2 mt-4'>
              <button
                onClick={() => setPendingReject(null)}
                className='px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg'
              >
                Annuler
              </button>
              <button
                disabled={rejectNote.trim().length < 10 || rejectMutation.isPending}
                onClick={() =>
                  rejectMutation.mutate({
                    id: pendingReject.id,
                    decisionNote: rejectNote.trim(),
                  })
                }
                className='px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-40'
              >
                {rejectMutation.isPending ? 'Refus en cours…' : 'Confirmer le refus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
